import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Alert, Paper } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthService } from '../services';
import PublicLayout from '../layouts/PublicLayout';
import { useTranslation } from 'react-i18next';

const validationSchema = (t: any) => Yup.object({
    email: Yup.string()
        .required(t('errors.required'))
        .email(t('errors.invalidEmail'))
});

const ResetPasswordRequest: React.FC = () => {
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();

    const formik = useFormik({
        initialValues: {
            email: ''
        },
        validationSchema: validationSchema(t),
        onSubmit: async (values) => {
            try {
                setError(null);
                await AuthService.requestPasswordReset({ email: values.email });
                setSuccess(true);
            } catch (err: any) {
                const errorMessage = err.response?.data?.error || err.message || t('auth.resetPasswordError');
                setError(errorMessage);
                console.error('Failed to request password reset:', err);
            }
        }
    });

    const content = success ? (
        <Box sx={{ mt: 8, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                    {t('auth.resetPasswordSuccess')}
                </Alert>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {t('auth.resetPasswordBody')}
                </Typography>
            </Paper>
        </Box>
    ) : (
        <Box sx={{ mt: 8, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
                <Typography component="h1" variant="h5" align="center" gutterBottom>
                    {t('auth.resetPassword')}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={formik.handleSubmit}>
                    <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label={t('auth.email')}
                        type="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        sx={{ mb: 2 }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? t('common.loading') : t('auth.resetPassword')}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );

    return (
        <PublicLayout>
            {content}
        </PublicLayout>
    );
};

export default ResetPasswordRequest;
